/**
 * Toddler activities for the Seedling developmental level.
 *
 * Following Steiner's first septennial (0-7, "The world is good"):
 * - Learning through imitation, movement, and the senses
 * - Rhythm as the carrier of life forces
 * - Warmth (physical and emotional) as the foundation
 * - Repetition as security, not boredom
 * - The adult as co-participant, not instructor
 *
 * Design principles for the Fire 7 landscape tablet:
 * - Touch targets ≥ 80px (toddler fingers are imprecise)
 * - Soft, warm colors (no harsh digital primaries)
 * - Sound and movement over text
 * - Parent/guardian reads aloud; child touches
 * - Activities last 1-3 minutes (toddler attention span)
 */

export type ActivityType =
  | 'rhythm-circle'
  | 'color-garden'
  | 'morning-verse'
  | 'counting-stones'
  | 'breathing-stone';

export interface ToddlerActivity {
  id: string;
  type: ActivityType;
  name: string;
  /** Short guidance for the parent, displayed briefly */
  parentNote: string;
  /** Emoji-free icon character for the card */
  icon: string;
  /** Warm description */
  description: string;
}

export const TODDLER_ACTIVITIES: ToddlerActivity[] = [
  {
    id: 'rhythm-1',
    type: 'rhythm-circle',
    name: 'Clapping Song',
    parentNote: 'Clap together in time with the pulsing circles. Your child follows your lead.',
    icon: '◌',
    description: 'A gentle rhythm to clap along with',
  },
  {
    id: 'garden-1',
    type: 'color-garden',
    name: 'Color Garden',
    parentNote: 'Touch anywhere to make colors bloom. Name the colors aloud as they appear.',
    icon: '❋',
    description: 'Touch the screen to grow soft colors',
  },
  {
    id: 'verse-1',
    type: 'morning-verse',
    name: 'Morning Verse',
    parentNote: 'Read each line aloud slowly. Tap to reveal the next line. Your voice is the instrument.',
    icon: '☀',
    description: 'A verse to begin the day together',
  },
  {
    id: 'stones-1',
    type: 'counting-stones',
    name: 'Counting Stones',
    parentNote: 'Touch each stone together, counting aloud: one, two, three, four, five.',
    icon: '◦',
    description: 'Touch each stone and count together',
  },
  {
    id: 'breath-1',
    type: 'breathing-stone',
    name: 'Breathing Stone',
    parentNote: 'Breathe together with the stone. In as it grows, out as it shrinks.',
    icon: '○',
    description: 'A calm breath together',
  },
];

// ---------------------------------------------------------------------------
// Morning verses (Steiner tradition: each school day begins with a verse)
// ---------------------------------------------------------------------------

export const MORNING_VERSES = [
  {
    id: 'sun-verse',
    title: 'The Sun',
    lines: [
      'The sun with loving light',
      'Makes bright for me each day.',
      'The soul with spirit power',
      'Gives strength unto my limbs.',
      'In sunlight shining clear',
      'I reverence, O God,',
      'The strength of humankind',
      'Which thou so graciously',
      'Hast planted in my soul,',
      'That I with all my might',
      'May love to work and learn.',
    ],
    attribution: '— Rudolf Steiner, morning verse for younger children',
  },
  {
    id: 'earth-verse',
    title: 'The Earth',
    lines: [
      'From my head to my feet',
      'I am the image of God.',
      'From my heart to my hands',
      'I feel the breath of God.',
      'When I speak with my mouth',
      'I follow God\'s will.',
      'When I see God everywhere,',
      'In mother, father, in all dear people,',
      'In beast and flower,',
      'In tree and stone,',
      'Nothing brings fear,',
      'But love to all',
      'That is around me.',
    ],
    attribution: '— Rudolf Steiner, morning verse for younger children',
  },
];

// ---------------------------------------------------------------------------
// Rhythm patterns (for the clapping game)
// ---------------------------------------------------------------------------

export interface RhythmBeat {
  /** Timing in ms from start of pattern */
  time: number;
  /** Which position (0-based, arranged in a circle) */
  position: number;
}

export const RHYTHM_PATTERNS = [
  {
    id: 'waltz',
    name: 'Waltz',
    bpm: 72,
    beats: 3,
    /** Simple 3-beat: ONE two three, ONE two three */
    pattern: [
      { time: 0, position: 0 },
      { time: 833, position: 1 },
      { time: 1666, position: 2 },
    ],
    loopMs: 2500,
  },
  {
    id: 'heartbeat',
    name: 'Heartbeat',
    bpm: 60,
    beats: 2,
    /** Two-beat: bump-BUMP, bump-BUMP */
    pattern: [
      { time: 0, position: 0 },
      { time: 500, position: 1 },
    ],
    loopMs: 1000,
  },
];

// ---------------------------------------------------------------------------
// Natural color palettes (Waldorf watercolor tones, no digital primaries)
// ---------------------------------------------------------------------------

export const GARDEN_COLORS = [
  '#d4a574', // warm ochre
  '#c4917a', // terracotta rose
  '#a8b89c', // sage green
  '#b8a0c4', // soft lavender
  '#c9b896', // wheat gold
  '#9ab4c4', // sky blue-grey
  '#d4b4a0', // sand
  '#b8c4a0', // spring green
  '#c4a4a4', // dusty rose
  '#a4b8b0', // seafoam
];
